import React, { useCallback, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Card, Steps, Form, Button, Space, Alert, message, Typography, Avatar } from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import DataCard from '../components/page/DataCard';
import { ArrowLeftOutlined, ArrowRightOutlined, RobotOutlined, SaveOutlined, ShopOutlined } from '@ant-design/icons';
import { apiTokenRequest, apiConfig } from '../authConfig';
import AdvisoryFirmBasicsFields from '../components/AdvisoryFirmBasicsFields';
import AdvisoryFirmBrandingFields from '../components/AdvisoryFirmBrandingFields';
import AdvisoryFirmPaymentLinksEditor from '../components/AdvisoryFirmPaymentLinksEditor';
import AdvisoryFirmAiDraftModal from '../components/AdvisoryFirmAiDraftModal';
import { useAdvisoryFirmAiDraft } from '../hooks/useAdvisoryFirmAiDraft';

const { Paragraph, Text } = Typography;

const STEP_FIELDS = [
    ['firmName', 'legalName', 'website', 'gstin', 'pan', 'linkedInUrl', 'address', 'city', 'state', 'pincode', 'country', 'aboutFirm', 'founderSummary'],
    ['brandPrimary', 'brandFont'],
    ['paymentLinks'],
];

/**
 * LA Onboarding — 3-step wizard (backlog Sheet-2 row 1, Workstream A).
 * Cites: .claude/design/la-onboarding-razorpay.md, Workstream A "Frontend flow".
 *
 * Shown by AdvisoryFirmSettings.jsx when GET /api/v1/me/advisory-firm reports no row yet.
 * Persona gating happens one level up (AdvisoryFirmSettings) — this component assumes it is
 * only ever mounted for an authenticated `lead_advisor`.
 *
 * BACKEND NOT YET LIVE this wave (routes.ts not wired, Prisma migration not run — see the
 * dispatching task). Every fetch below targets the documented contract exactly as if it were
 * live; failures are caught and surfaced as an explicit "not available yet" state, never
 * silently swallowed or faked as success (Commandment VI — no state-changing action may look
 * like it succeeded when it didn't).
 */
const AdvisoryFirmOnboarding = ({ onCreated }) => {
    const { instance, accounts } = useMsal();
    const [form] = Form.useForm();
    const [step, setStep] = useState(0);
    const [logoUrl, setLogoUrl] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const getApiToken = useCallback(async () => {
        const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account: accounts[0] });
        return tokenResponse.accessToken;
    }, [instance, accounts]);

    const { aiModal, runAiEnrichment, applyDraftField, applyAllDraftFields, closeAiModal } =
        useAdvisoryFirmAiDraft(form, getApiToken);

    const goNext = async () => {
        try {
            await form.validateFields(STEP_FIELDS[step]);
            setStep((s) => Math.min(s + 1, 2));
        } catch {
            /* antd surfaces the field errors inline */
        }
    };
    const goBack = () => setStep((s) => Math.max(s - 1, 0));

    // --- Final save ----------------------------------------------------------------------------
    const handleSave = async () => {
        try {
            await form.validateFields(STEP_FIELDS[2]);
        } catch {
            return;
        }
        setSaving(true);
        setSaveError(null);
        try {
            const values = form.getFieldsValue(true);
            const paymentLinks = (values.paymentLinks || []).filter((p) => p?.label && p?.url);
            const payload = {
                firmName: values.firmName,
                legalName: values.legalName || null,
                gstin: values.gstin || null,
                pan: values.pan || null,
                address: values.address || null,
                city: values.city || null,
                state: values.state || null,
                country: values.country || 'India',
                pincode: values.pincode || null,
                website: values.website || null,
                linkedInUrl: values.linkedInUrl || null,
                logoBlobUrl: logoUrl,
                brandPrimary: values.brandPrimary || null,
                brandFont: values.brandFont || null,
                aboutFirm: values.aboutFirm || null,
                founderProfileJson: values.founderSummary ? JSON.stringify({ summary: values.founderSummary }) : null,
                paymentLinksJson: JSON.stringify(paymentLinks),
            };
            const token = await getApiToken();
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/me/advisory-firm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            const json = await res.json().catch(() => null);
            if (!res.ok || !json || !json.success) {
                throw new Error(
                    json?.error?.message ||
                    `Save failed (${res.status}). This route isn't wired on the backend yet this wave.`,
                );
            }
            message.success('Firm profile saved.');
            onCreated?.(json.data?.firm || json.data);
        } catch (err) {
            console.warn('advisory firm create failed', err);
            setSaveError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <PageShell width="narrow">
            <PageHeader
                title="Set up your advisory firm"
                subtitle="A one-time profile for your firm - clients will see this branding on the artefacts you generate for them."
            />

            <Steps
                current={step}
                items={[{ title: 'Firm basics' }, { title: 'Branding' }, { title: 'Preview & save' }]}
            />

            <DataCard>
                <Form form={form} layout="vertical" initialValues={{ country: 'India', paymentLinks: [] }}>
                    <div style={{ display: step === 0 ? 'block' : 'none' }}>
                        <AdvisoryFirmBasicsFields />
                        <Button icon={<RobotOutlined />} onClick={runAiEnrichment}>
                            Auto-fetch with AI
                        </Button>
                    </div>

                    <div style={{ display: step === 1 ? 'block' : 'none' }}>
                        <AdvisoryFirmBrandingFields getApiToken={getApiToken} logoUrl={logoUrl} onLogoUploaded={setLogoUrl} />
                    </div>

                    <div style={{ display: step === 2 ? 'block' : 'none' }}>
                        <Form.Item shouldUpdate style={{ marginBottom: 20 }}>
                            {() => {
                                const v = form.getFieldsValue(true);
                                return (
                                    <Card size="small" style={{ background: 'var(--color-surface-sunken)' }}>
                                        <Space align="start">
                                            <Avatar shape="square" size={48} src={logoUrl || undefined} icon={!logoUrl && <ShopOutlined />} />
                                            <div>
                                                <Text style={{ fontWeight: 800, fontSize: 16, color: v.brandPrimary || undefined, fontFamily: v.brandFont || undefined }}>
                                                    {v.firmName || 'Your firm name'}
                                                </Text>
                                                <Paragraph style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
                                                    {v.aboutFirm || 'About-firm text will appear here once added.'}
                                                </Paragraph>
                                            </div>
                                        </Space>
                                    </Card>
                                );
                            }}
                        </Form.Item>

                        <AdvisoryFirmPaymentLinksEditor />

                        {saveError && (
                            <Alert
                                style={{ marginTop: 16 }}
                                type="error"
                                showIcon
                                message="Could not save firm profile"
                                description={saveError}
                            />
                        )}
                    </div>
                </Form>
            </DataCard>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <Button icon={<ArrowLeftOutlined />} disabled={step === 0} onClick={goBack}>
                    Back
                </Button>
                {step < 2 ? (
                    <Button type="primary" onClick={goNext}>
                        Next <ArrowRightOutlined />
                    </Button>
                ) : (
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                        Save & finish
                    </Button>
                )}
            </div>

            <AdvisoryFirmAiDraftModal
                open={aiModal.open}
                loading={aiModal.loading}
                error={aiModal.error}
                draft={aiModal.draft}
                appliedKeys={aiModal.appliedKeys}
                onApply={applyDraftField}
                onApplyAll={applyAllDraftFields}
                onClose={closeAiModal}
            />
        </PageShell>
    );
};

export default AdvisoryFirmOnboarding;
